import { Add, Close } from "@mui/icons-material";
import { Box, ButtonBase, IconButton, LinearProgress, Typography } from "@mui/material";
import { useContext, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { OwnerConsoleViewController } from "../../controllers/OwnerConsoleViewController";
import { formatCmpctNumber } from "../../helpers/utils";
import { OwnerAdjustment } from "../../interfaces/AppDefinitions";
import { NewAdjustment } from "../NewAdjustment";
import { INK, INK_2, LINE, MUTED, cardSx, withAlpha } from "./shared";

// Home updates (owner adjustments): what the homeowner has told us about their home, plus the
// button that opens the same add form as the original report.
export default function HomeUpdates() {
	const { appState, appDispatch } = useContext(AppContext);
	const [showModal, toggleModal] = useState(false);
	// Where the button was tapped, so the form opens right there (the report can be a tall iframe).
	const [anchor, setAnchor] = useState<DOMRect | null>(null);
	const { report_configuration, currentAddrId, isFetchingAdjustments, colorPallete } = appState;
	const { owner_adjustments } = report_configuration.find((x) => x.addrId === currentAddrId) ?? {};
	const accent = colorPallete["scheme3"];

	async function handleDelete(adjustment: OwnerAdjustment) {
		if (!window.confirm(`Remove the ${adjustment.adjustment_type.toLowerCase()} update?`)) return;
		try {
			appDispatch({ type: "SET_FETCHING_ADJUSTMENTS", value: true });
			await OwnerConsoleViewController.DeleteAdjustment({
				adjustment_type_id: adjustment.adjustment_type_id,
				addrId: adjustment.addrid,
				enc: appState.enc,
				track: appState.trackView,
			});
			const { report_configuration } = await OwnerConsoleViewController.GetReportConfiguration(appState.enc);
			if (report_configuration) {
				appDispatch({ type: "UPDATE_STATE", key: "report_configuration", value: report_configuration });
			}
		} finally {
			appDispatch({ type: "SET_FETCHING_ADJUSTMENTS", value: false });
		}
	}

	return (
		<Box sx={{ ...cardSx, p: "18px 20px" }}>
			<NewAdjustment show={showModal} toggleModal={toggleModal} anchor={anchor} />
			<Typography component="h2" sx={{ fontSize: "14px", fontWeight: 700, color: INK }}>
				Renovated or added a room?
			</Typography>
			<Typography sx={{ fontSize: "13px", color: INK_2, mt: "4px" }}>
				Update your home details to sharpen the estimate.
			</Typography>

			{isFetchingAdjustments && <LinearProgress sx={{ mt: "12px", borderRadius: "4px" }} />}
			{!isFetchingAdjustments && owner_adjustments && owner_adjustments.length > 0 && (
				<Box component="ul" sx={{ listStyle: "none", p: 0, m: "12px 0 0", borderTop: `1px solid ${LINE}` }}>
					{owner_adjustments.map((row) => (
						<Box
							component="li"
							key={row.adjustment_type_id}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: "8px",
								py: "8px",
								borderBottom: `1px solid ${LINE}`,
								fontSize: "13px",
							}}
						>
							<Box sx={{ flex: 1, minWidth: 0, color: INK }}>{row.adjustment_type}</Box>
							<Box sx={{ color: MUTED, fontVariantNumeric: "tabular-nums" }}>
								{formatCmpctNumber(row.adjustment_value)}
							</Box>
							<IconButton
								size="small"
								aria-label={`Remove ${row.adjustment_type}`}
								onClick={() => handleDelete(row)}
								sx={{ color: MUTED }}
							>
								<Close fontSize="small" />
							</IconButton>
						</Box>
					))}
				</Box>
			)}

			<ButtonBase
				onClick={(e) => {
					setAnchor(e.currentTarget.getBoundingClientRect());
					toggleModal(true);
				}}
				disabled={isFetchingAdjustments}
				sx={{
					mt: "12px",
					px: "12px",
					py: "7px",
					gap: "4px",
					borderRadius: "9px",
					border: `1.5px solid ${accent}`,
					color: accent,
					fontSize: "13px",
					fontWeight: 700,
					"&:hover": { backgroundColor: withAlpha(accent, 0.06) },
				}}
			>
				<Add fontSize="small" /> Add a home update
			</ButtonBase>
		</Box>
	);
}
